import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  MsalBroadcastService,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import {
  EventMessage,
  EventType,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'auth-login',
  imports: [MsalModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  role: 'client' | 'producer' = 'client';
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly _destroying$ = new Subject<void>();

  get roleLabel() {
    return this.role == 'client' ? 'cliente' : 'intermediario';
  }

  ngOnInit() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.router.navigate(['/dashboard']);
        } else if (this.shouldStartSignUp()) {
          // Deep link such as /?action=registrate: go straight to the B2C
          // sign-up flow. B2C returns to the bare redirectUri, so the query
          // param is gone on the way back and this cannot loop.
          this.register();
        }
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          let resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority:
              environment.b2cPolicies.authorities.resetPassword.authority,
            scopes: [],
          };

          this.login(resetPasswordFlowRequest);
        }
      });
    this.msalService.handleRedirectObservable().subscribe(); // Subscribing to handleRedirectObservable before any other functions both initializes the application and ensures redirects are handled
  }

  login(request?: RedirectRequest | PopupRequest) {
    this.msalService.loginRedirect(request);
  }

  private shouldStartSignUp(): boolean {
    return this.route.snapshot.queryParamMap.get('action') === 'registrate';
  }

  register() {
    this.msalService.loginRedirect({
      authority:
        environment.b2cPolicies.authorities.signUp.authority[
          this.role == 'client' ? 'client' : 'producer'
        ],
      scopes: environment.apiConfig.scopes,
    });
  }

  ngOnDestroy() {
    this._destroying$.next();
  }
}
